// ResourceCard
//
// Card individual para listar recursos de Formacion en el panel admin
// (videos y PDFs). Muestra miniatura/portada o icono por tipo, a quien va
// dirigido, titulo + descripcion (truncada) y un menu de acciones
// (abrir, copiar URL, editar, borrar).
//
// Componente compartido por REVI y K12: las audiencias se pasan por props.

import React from "react";
import { Dropdown, Tag, Tooltip, message } from "antd";
import {
  PlayCircleOutlined,
  FilePdfOutlined,
  MoreOutlined,
  ExportOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styles from "./ResourceCard.module.scss";

const TYPE_ICON = {
  video: PlayCircleOutlined,
  pdf: FilePdfOutlined,
};

const TYPE_LABEL = {
  video: "Vídeo",
  pdf: "PDF",
};

export function ResourceCard({
  resource,
  onEdit,
  onDelete,
  audienceOptions = [],
}) {
  const Icon = TYPE_ICON[resource.type] || FilePdfOutlined;
  const externalUrl = resource.type === "video" ? resource.url : resource.file;

  const audienceTags = (resource.audiences || []).map(
    (value) => audienceOptions.find((o) => o.value === value)?.label || value,
  );

  const open = () => {
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
    } else {
      message.warning("Este recurso no tiene URL/fichero asociado");
    }
  };

  const handleCopy = () => {
    if (!externalUrl) {
      message.warning("Nada que copiar");
      return;
    }
    try {
      navigator.clipboard.writeText(externalUrl);
      message.success("URL copiada");
    } catch (e) {
      message.error("No se pudo copiar");
    }
  };

  const menuItems = [
    {
      key: "open",
      icon: <ExportOutlined />,
      label: resource.type === "video" ? "Abrir vídeo" : "Abrir PDF",
      onClick: open,
    },
    {
      key: "copy",
      icon: <CopyOutlined />,
      label: "Copiar URL",
      onClick: handleCopy,
    },
  ];

  if (onEdit) {
    menuItems.push({
      key: "edit",
      icon: <EditOutlined />,
      label: "Editar",
      onClick: () => onEdit(resource),
    });
  }
  if (onDelete) {
    menuItems.push({ type: "divider" });
    menuItems.push({
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Borrar",
      danger: true,
      onClick: () => onDelete(resource),
    });
  }

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.cover}
        onClick={open}
        aria-label={`Abrir ${resource.title}`}
      >
        {resource.image ? (
          <img
            src={resource.image}
            alt={resource.title}
            className={styles.coverImg}
            loading="lazy"
          />
        ) : (
          <Icon className={styles.coverIcon} />
        )}

        <div className={styles.coverOverlay}>
          <Icon className={styles.coverPlay} />
        </div>
      </button>

      <div className={styles.badges}>
        <Tag
          color={resource.type === "video" ? "geekblue" : "volcano"}
          icon={<Icon />}
        >
          {TYPE_LABEL[resource.type] || resource.type}
        </Tag>
      </div>

      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button type="button" className={styles.menuBtn} aria-label="Acciones">
          <MoreOutlined />
        </button>
      </Dropdown>

      <div className={styles.body}>
        <Tooltip title={resource.title}>
          <div className={styles.title}>{resource.title}</div>
        </Tooltip>
        <Tooltip title={resource.description}>
          <div className={styles.description}>{resource.description}</div>
        </Tooltip>
        {audienceTags.length ? (
          <div className={styles.audiences}>
            {audienceTags.map((label) => (
              <Tag key={label} bordered={false} color="blue">
                {label}
              </Tag>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
